import { SentryHelper } from "@app/helpers/SentryHelper";
import { AxiosUtil } from "@app/utils/AxiosUtil";
import {
    CustomEndpointInterface,
    CreateCustomEndpointDto,
    UpdateCustomEndpointDto,
} from "@dashart/dashart-gpt-shared-library";
import { AxiosInstance, AxiosResponse } from "axios";

/**
 * Static service wrapping the CRUD endpoints at /v1/custom-endpoints.
 * All methods reuse a lazily-created preconfigured axios instance.
 */
export class CustomEndpointService {

    private static axiosInstance: AxiosInstance;

    /**
     * Returns a lazily-created preconfigured axios instance.
     * Internal helper used by every public method.
     *
     * @returns the shared AxiosInstance
     */
    private static getAxiosInstance(): AxiosInstance {
        if (!this.axiosInstance) {
            this.axiosInstance = AxiosUtil.createPreconfiguredAxiosInstance();
        }
        return this.axiosInstance;
    }

    /**
     * Loads all custom endpoints for the current user.
     * Backed by GET /v1/custom-endpoints.
     *
     * @returns Promise resolving to the user's CustomEndpointInterface array
     */
    public static async getAll(): Promise<CustomEndpointInterface[]> {
        return this.getAxiosInstance().get(`/v1/custom-endpoints`).then(
            (response: AxiosResponse) => Promise.resolve((response.data ?? []) as CustomEndpointInterface[]),
            (error) => {
                console.log('Get custom endpoints failed', error);
                SentryHelper.apiCatch(error);
                return Promise.reject(error?.response?.data ?? error);
            }
        );
    }

    /**
     * Loads a single custom endpoint by numeric id.
     * Backed by GET /v1/custom-endpoints/:id.
     *
     * @param id numeric endpoint id
     * @returns Promise resolving to the endpoint
     */
    public static async getById(id: number): Promise<CustomEndpointInterface> {
        return this.getAxiosInstance().get(`/v1/custom-endpoints/${id}`).then(
            (response: AxiosResponse) => Promise.resolve(response.data as CustomEndpointInterface),
            (error) => {
                console.log('Get custom endpoint failed', error);
                SentryHelper.apiCatch(error);
                return Promise.reject(error?.response?.data ?? error);
            }
        );
    }

    /**
     * Creates a new custom endpoint for the current user.
     * Backed by POST /v1/custom-endpoints.
     *
     * @param dto validated CreateCustomEndpointDto payload
     * @returns Promise resolving to the persisted endpoint
     */
    public static async create(dto: CreateCustomEndpointDto): Promise<CustomEndpointInterface> {
        return this.getAxiosInstance().post(`/v1/custom-endpoints`, dto).then(
            (response: AxiosResponse) => Promise.resolve(response.data as CustomEndpointInterface),
            (error) => {
                console.log('Create custom endpoint failed', error);
                SentryHelper.apiCatch(error);
                return Promise.reject(error?.response?.data ?? error);
            }
        );
    }

    /**
     * Applies a partial update to a custom endpoint after stripping server-managed
     * fields (id, userId, companyId, timestamps) from the payload.
     * Backed by PATCH /v1/custom-endpoints/:id.
     *
     * @param id numeric endpoint id
     * @param dto validated UpdateCustomEndpointDto payload (may include extra entity fields, which are stripped)
     * @returns Promise resolving to the updated endpoint
     */
    public static async update(id: number, dto: UpdateCustomEndpointDto): Promise<CustomEndpointInterface> {
        const payload: any = { ...dto };
        delete payload.id;
        delete payload.userId;
        delete payload.companyId;
        delete payload.createdAt;
        delete payload.updatedAt;
        delete payload.deletedAt;

        return this.getAxiosInstance().patch(`/v1/custom-endpoints/${id}`, payload).then(
            (response: AxiosResponse) => Promise.resolve(response.data as CustomEndpointInterface),
            (error) => {
                console.log('Update custom endpoint failed', error);
                SentryHelper.apiCatch(error);
                return Promise.reject(error?.response?.data ?? error);
            }
        );
    }

    /**
     * Soft-deletes a custom endpoint owned by the current user.
     * Backed by DELETE /v1/custom-endpoints/:id.
     *
     * @param id numeric endpoint id
     * @returns Promise resolving once the delete completes
     */
    public static async delete(id: number): Promise<void> {
        return this.getAxiosInstance().delete(`/v1/custom-endpoints/${id}`).then(
            () => Promise.resolve(),
            (error) => {
                console.log('Delete custom endpoint failed', error);
                SentryHelper.apiCatch(error);
                return Promise.reject(error?.response?.data ?? error);
            }
        );
    }
}
